import { ReactElement } from 'react';
import { Calculator } from '@methodset/calculator-ts';
import { Applet, AppletPanel, ColumnWidgetConfiguration, JustificationType, RowJustifyType, Widget, WidgetType, Width } from '@methodset/application-client-ts';
import { Configuration } from '@methodset/model-client-ts';
import { Col, Row } from 'antd';
import { ConfigurationCallback, WidgetViewer } from '../WidgetViewer/WidgetViewer';
import { ItemMenu, WidgetCallback } from 'containers/Console/Models/ModelItem/ModelApplications/ApplicationItem/ModelApplet/AppletEditor/ItemMenu/ItemMenu';
import { Globals } from 'constants/Globals';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { CoreUtils } from 'utils/CoreUtils';
import classNames from 'classnames';
import update from 'immutability-helper';
import './ColumnWidgetViewer.less';

export type ColumnWidgetViewerProps = {
    applet: Applet,
    panel: AppletPanel,
    widget: Widget,
    calculator: Calculator,
    extra?: ReactElement,
    configuration: ColumnWidgetConfiguration,
    // model: Model,
    // modelSetup?: ModelSetup,
    // modelId: string,
    // version?: number
    appletConfiguration: Configuration,
    //variableSpecs?: VariableSpec[],
    showHeader: boolean,
    onEdit?: WidgetCallback,
    onRemove?: WidgetCallback,
    onChange?: WidgetCallback,
    onUpdate: ConfigurationCallback
}

export const ColumnWidgetViewer = (props: ColumnWidgetViewerProps): ReactElement => {

    const findWidget = (widgetId: string): Widget | undefined => {
        return props.panel.widgets.find(widget => widget.id === widgetId);
    }

    const findIndex = (widgetId: string): number => {
        return props.configuration.widgetIds.findIndex(id => id === widgetId);
    }

    const buildView = (index: number, widget: Widget, menu: ReactElement | undefined): ReactElement => {
        return (
            <WidgetViewer
                key={`${widget.id}-${index}`}
                // model={props.model}
                applet={props.applet}
                panel={props.panel}
                widget={widget}
                calculator={props.calculator}
                extra={menu}
                //modelSetup={props.modelSetup}
                // modelId={props.modelId}
                // version={props.version}
                configuration={props.appletConfiguration}
                //variableSpecs={props.variableSpecs}
                showHeader={props.showHeader}
                onEdit={props.onEdit}
                onRemove={props.onRemove}
                onChange={props.onChange}
                onUpdate={props.onUpdate}
            />
        )
    }

    const handleMoveUp = (widget: Widget): void => {
        const index = findIndex(widget.id);
        if (index === -1) {
            return;
        }
        const configuration = update(props.configuration, {
            widgetIds: {
                $splice: [[index, 1], [index - 1, 0, widget.id]]
            }
        });
        widget = update(props.widget, {
            configuration: { $set: configuration }
        });
        props.onChange!(widget);
    }

    const handleMoveDown = (widget: Widget): void => {
        const index = findIndex(widget.id);
        if (index === -1) {
            return;
        }
        const configuration = update(props.configuration, {
            widgetIds: {
                $splice: [[index, 1], [index + 1, 0, widget.id]]
            }
        });
        widget = update(props.widget, {
            configuration: { $set: configuration }
        });
        props.onChange!(widget);
    }

    const toSpan = (width: Width | undefined): number | undefined => {
        if (CoreUtils.isNumber(width)) {
            return Globals.LAYOUT_SCALE * width;
        } else {
            return undefined;
        }
    }

    // const toOffset = (width: Width | undefined, justification: JustificationType | undefined): number | undefined => {
    //     if (CoreUtils.isNumber(width)) {
    //         if (justification === JustificationType.LEFT) {
    //             return 0;
    //         } else if (justification === JustificationType.CENTER) {
    //             return Globals.LAYOUT_COLUMNS - width;
    //         } else if (justification === JustificationType.RIGHT) {
    //             return (Globals.LAYOUT_COLUMNS - width) * 2;
    //         }
    //     } else {
    //         return undefined;
    //     }
    // }

    const toJustify = (justification: JustificationType | undefined): any => {
        if (!justification) {
            return undefined;
        }
        switch (justification) {
            case JustificationType.LEFT:
                return CoreUtils.toLower(RowJustifyType[RowJustifyType.START]);
            case JustificationType.CENTER:
                return CoreUtils.toLower(RowJustifyType[RowJustifyType.CENTER]);
            case JustificationType.RIGHT:
                return CoreUtils.toLower(RowJustifyType[RowJustifyType.END]);
            default:
                return undefined;
        }
    }

    // const span = !CoreUtils.isEmpty(props.configuration.span) ? props.configuration.span * Globals.LAYOUT_SCALE : undefined;
    // const offset = !CoreUtils.isEmpty(props.configuration.offset) ? props.configuration.offset * Globals.LAYOUT_SCALE : undefined;
    const gap = Globals.spacing(props.configuration.gap);

    return (
        // Outer column has been hoisted up into the widget viewer.
        //<Col className="x-rowwidgetviewer-col" span={span} offset={offset}>
        <Row className="x-columnwidgetviewer-fill" gutter={[0, gap]}>
            {props.configuration.widgetIds.map((widgetId, index) => {
                const widget = findWidget(widgetId);
                if (!widget) {
                    return undefined;
                } else {
                    let menu;
                    if (props.showHeader) {
                        const extra = [{
                            icon: <ArrowUpOutlined />,
                            label: "Move up",
                            disabled: () => index === 0,
                            onSelect: handleMoveUp
                        }, {
                            icon: <ArrowDownOutlined />,
                            label: "Move down",
                            disabled: () => index === props.configuration.widgetIds.length - 1,
                            onSelect: handleMoveDown
                        }];
                        menu = (
                            <ItemMenu
                                widget={widget}
                                extra={extra}
                                onEdit={props.onEdit!}
                                onRemove={props.onRemove!}
                            />
                        )
                    }
                    if (widget.configuration.type === WidgetType.ROW || widget.configuration.type === WidgetType.COLUMN) {
                        return buildView(index, widget, menu);
                    } else {
                        return (
                            <Col
                                key={`column-${index}`}
                                span={24}
                            >
                                <Row className="x-columnwidgetviewer-fill" justify={toJustify(props.configuration.justification)} wrap={false}>
                                    <Col
                                        className={classNames({ "x-columnwidgetviewer-fill": widget.configuration.width === "FILL" })}
                                        span={toSpan(widget.configuration.width)}
                                    //offset={toOffset(widget.configuration.width, props.configuration.justification)}
                                    >
                                        {buildView(index, widget, menu)}
                                    </Col>
                                </Row>
                            </Col>
                        )
                    }
                }
            })}
        </Row>
    );

}
